import { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { routes } from '../routes/routes';
import styled from 'styled-components';
import Branding from './Branding';

import Arrow from '../assets/Arrow.svg';

// Constants
const DEFAULT_COLORS = {
	toggleColor: '#C9B815',
	toggleActiveColor: '#A3CBC0',
	fullNavColor: '#767727',
};

// Styled Components
const NavigationToggle = styled.button`
	position: relative;
	cursor: pointer;
	color: white;
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 8px;
	background: none;
	border: none;
	padding: 0;
	.toggle-icon {
		height: 38px;
		width: 38px;
		background: ${(props) => (props.$isOpen ? props.$activeColor : props.$color)};
		transition: background-color 0.3s ease;
	}
`;

const NavigationFull = styled.div`
	position: fixed;
	inset: 0;
	background: ${(props) => props.$backgroundColor};
	transition:
		background-color 0.3s ease,
		clip-path 0.8s ease;
	clip-path: polygon(50% 50%, 50% 50%, 50% 50%, 50% 50%);
	display: grid;
	grid-template-areas: 'stack';
	z-index: 100;

	&.active {
		pointer-events: none;
	}

	.inner-header {
		position: relative;
		grid-area: stack;
	}

	.inner-toggle {
		position: relative;
	}

	.navigation-full-content {
		position: relative;
		display: grid;
		grid-template-areas: 'stack';
		place-items: center;
		grid-area: stack;
	}
`;

const NavigationMenu = styled.div`
	position: relative;
	grid-area: stack;
	margin-top: var(--header-height);
	padding-bottom: 14vh;

	ul {
		display: flex;
		flex-direction: column;
		justify-content: center;
		text-align: left;
		a {
			pointer-events: all;
			opacity: 0.45;
			&:hover,
			&.active {
				opacity: 1;
			}
		}
	}
`;

const NavigationFrameContainer = styled.div`
	grid-area: stack;
	height: 100svh;
	width: 100svw;
	display: grid;
	place-items: center;
`;

const NavigationFrame = styled.div`
	position: relative;
	height: 64svh;
	width: ${(props) => `min(100%, calc(64svh * ${props.$aspectRatio}))`};
	transform-origin: center center;
`;

const ClipPath = styled.div`
	position: absolute;
	aspect-ratio: 1/1;
	background: rgba(255, 255, 255, 0.1);
	height: ${(props) => props.$height}%;
	width: ${(props) => props.$width}%;
	left: ${(props) => (props.$x / props.$containerWidth) * 100}%;
	top: ${(props) => (props.$y / props.$containerHeight) * 100}%;
`;

// Hook for frame dimensions
import { useFrameDimensions, getPathValues } from '../utils/clipPath';

const ClipPaths = ({ clipPaths, dimensions }) => (
	<>
		{dimensions.width > 0 &&
			clipPaths.map((path, index) => {
				const values = getPathValues(path.initial, dimensions);
				return <ClipPath key={index} $x={values.x} $y={values.y} $width={values.width} $containerWidth={dimensions.width} $containerHeight={dimensions.height} />;
			})}
	</>
);

const NavigationLink = ({ path, name, currentPath, onMouseEnter, className, onNavigate }) => {
	const isActive = path === currentPath;
	const navigate = useNavigate();

	const handleClick = async (e) => {
		e.preventDefault();
		if (onNavigate) {
			// Navigate first, but don't close the menu yet
			navigate(path);

			// Wait a bit for the new route to load
			await new Promise((resolve) => setTimeout(resolve, 300));

			// Then trigger the closing animation
			onNavigate();
		}
	};

	return (
		<li>
			<Link to={path} className={`${className || ''} ${isActive ? 'active' : ''}`} onMouseEnter={() => onMouseEnter(path)} onClick={handleClick}>
				{name}
			</Link>
		</li>
	);
};

const Navigation = () => {
	const location = useLocation();
	const navItems = routes.filter((route) => route.showInNav);
	const [isOpen, setIsOpen] = useState(false);
	const [hoveredPath, setHoveredPath] = useState(location.pathname);
	const toggleRef = useRef(null);
	const fullNavRef = useRef(null);
	const toggleIconRef = useRef(null);
	const [shouldClose, setShouldClose] = useState(false);

	// Get current route colors and dimensions with defaults
	const currentRoute = routes.find((route) => route.path === location.pathname) || routes[0];
	const hoveredRoute = hoveredPath ? routes.find((route) => route.path === hoveredPath) : null;
	const activeRoute = hoveredRoute || currentRoute;

	const {
		toggleColor,
		toggleActiveColor,
		fullNavColor,
		clipPaths = [],
		masthead,
	} = {
		...DEFAULT_COLORS,
		...activeRoute,
	};

	const updateNavigationClipPath = useCallback((isExpanded = false) => {
		if (!toggleIconRef.current || !fullNavRef.current) {
			console.warn('❌ Missing refs:', {
				toggleIcon: !!toggleIconRef.current,
				fullNav: !!fullNavRef.current,
			});
			return;
		}

		const rect = toggleIconRef.current.getBoundingClientRect();

		const originX = rect.left + rect.width / 2;
		const originY = rect.top + rect.height / 2;

		if (isExpanded) {
			const maxDimension = Math.max(Math.max(originX, window.innerWidth - originX), Math.max(originY, window.innerHeight - originY));
			const expandSize = maxDimension * 2;
			fullNavRef.current.style.clipPath = `polygon(
				${originX - expandSize}px ${originY - expandSize}px,
				${originX + expandSize}px ${originY - expandSize}px,
				${originX + expandSize}px ${originY + expandSize}px,
				${originX - expandSize}px ${originY + expandSize}px
			)`;
			fullNavRef.current.classList.add('active');
		} else {
			fullNavRef.current.style.clipPath = `polygon(
				${originX}px ${originY}px,
				${originX}px ${originY}px,
				${originX}px ${originY}px,
				${originX}px ${originY}px
			)`;
			fullNavRef.current.classList.remove('active');
		}
	}, []);

	const toggleNavigation = useCallback(
		(forcedState) => {
			// Ensure forcedState is treated as a boolean
			const newState = typeof forcedState === 'boolean' ? forcedState : !isOpen;

			setIsOpen(newState);
			updateNavigationClipPath(newState);
		},
		[updateNavigationClipPath, isOpen]
	);

	// Initialize clip path
	useEffect(() => {
		if (toggleIconRef.current && fullNavRef.current) {
			updateNavigationClipPath(false);
		}
	}, [updateNavigationClipPath]);

	// Handle body overflow
	useEffect(() => {
		document.body.style.overflow = isOpen ? 'hidden' : '';
		return () => {
			document.body.style.overflow = '';
		};
	}, [isOpen]);

	const frameDimensions = useFrameDimensions(fullNavRef, masthead?.dimensions);
	const aspectRatio = masthead?.dimensions ? masthead.dimensions.width / masthead.dimensions.height : 1.5;

	const handleMouseEnter = (path) => {
		setHoveredPath(path);
	};

	// First, let's log in the navigation item click handler
	const handleNavItemClick = useCallback(() => {
		setIsOpen(false);
		updateNavigationClipPath(false);
	}, [updateNavigationClipPath]);

	// Add effect to handle navigation closing
	useEffect(() => {
		if (shouldClose) {
			setIsOpen(false);
			updateNavigationClipPath(false);
			setShouldClose(false);
		}
	}, [shouldClose, updateNavigationClipPath]);

	return (
		<nav>
			<NavigationToggle ref={toggleRef} className="navigation-toggle" onClick={toggleNavigation} $color={toggleColor} $activeColor={toggleActiveColor} $isOpen={isOpen} aria-expanded={isOpen} aria-controls="navigation-menu" aria-label={isOpen ? 'Close menu' : 'Open menu'}>
				<span className="relative text-white mix-blend-difference">Menu</span>
				<div className="flex justify-center items-center toggle-icon" ref={toggleIconRef}>
					<img src={Arrow} alt="Arrow" className="max-w-[17px]" />
				</div>
			</NavigationToggle>

			<NavigationFull ref={fullNavRef} className="navigation-full" $backgroundColor={fullNavColor}>
				<div className="inner-header">
					<div className="container pt-[2rem] relative z-[9999]">
						<div className="flex justify-between items-center">
							<div className="flex items-center">
								<Branding
									$isInner={true}
									isInner={true}
									className="mix-blend-normal"
									brandingClassName="text-white mix-blend-normal"
									taglineClassName="text-white"
									onLinkClick={() => {
										setIsOpen(false);
										updateNavigationClipPath(false);
									}}
								/>
							</div>
							<div className="flex items-center">
								<NavigationToggle className="inner-toggle" onClick={toggleNavigation} $color={toggleColor} $activeColor={toggleActiveColor} $isOpen={isOpen} aria-expanded={isOpen} aria-controls="navigation-menu" aria-label={isOpen ? 'Close menu' : 'Open menu'}>
									<span>Menu</span>
									<div className="flex justify-center items-center toggle-icon">
										<img src={Arrow} alt="Arrow" className="max-w-[17px]" />
									</div>
								</NavigationToggle>
							</div>
						</div>
					</div>
				</div>
				<div className="navigation-full-content">
					<NavigationFrameContainer>
						<NavigationFrame ref={fullNavRef} $aspectRatio={aspectRatio}>
							<ClipPaths clipPaths={clipPaths} dimensions={frameDimensions} />
						</NavigationFrame>
					</NavigationFrameContainer>

					<NavigationMenu className="navigation-menu" id="navigation-menu">
						<ul className="gap-6 lg:gap-8">
							{navItems.map((item) => (
								<NavigationLink key={item.path} path={item.path} name={item.name} currentPath={location.pathname} onMouseEnter={handleMouseEnter} className="lgtext-[2.25] text-[1.875rem] font-bold tracking-widest text-white uppercase font-trade-compressed leading-[1]" onNavigate={handleNavItemClick} />
							))}
						</ul>
					</NavigationMenu>
				</div>
			</NavigationFull>
		</nav>
	);
};

export default Navigation;
